/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/stores/provider.linear.store.ts
 */

import { derived, get } from 'svelte/store';
import { isAxiosError } from 'axios';
import type { LinearProviderData, LinearTeamsResponse } from '@repo/common/types/Linear';
import { getPrivateClient } from '../data/common.data';
import { userAuthStore } from './auth.store';
import { createStoreHandle, type DataModel } from '../utils/store.util';
import { waitForAuthAndUserId } from './waitForAuth';
import constants from '../utils/constants';

const FILENAME = '.providers.linear.dat';

interface LinearProviderState {
	connected: boolean;
	error: string | null;
	providers: Array<{
		id: string;
		email: string;
		data: LinearProviderData;
	}>;
	teamsData: LinearTeamsResponse | null;
}

const dummyLinearState: LinearProviderState = {
	connected: false,
	error: null,
	providers: [],
	teamsData: null
};

const UPDATE_FREQUENCY = 5 * 60 * 1000; // 5 minutes in milliseconds

const handleLinearError = (message: string) => {
	console.warn('[Linear Store] Error:', message);
};

// Create the Linear store handle
const {
	store: linearProviderDataModel,
	itemStore: linearProviderStore,
	setItem: setLinearProviderState,
	resetItem: resetLinearProviderState,
	updateItem: refreshLinearData,
	loadStoredItem: loadStoredLinearData
} = createStoreHandle<LinearProviderState>(
	FILENAME,
	dummyLinearState,
	{
		updateTimeout: 5 * 60 * 1000, // 5 minutes
		retryTimeout: 30 * 1000 // 30 seconds
	},
	async (): Promise<Omit<DataModel<LinearProviderState>, 'status' | 'lastUpdated'> | undefined> => {
		const $linearProviderDataModel = get(linearProviderDataModel);

		// Fetch providers
		let providers: LinearProviderState['providers'] = [];
		let teamsData: LinearTeamsResponse | null = null;
		let error: string | null = null;

		try {
			const authToken = get(userAuthStore);
			const client = getPrivateClient(authToken, { timeout: constants.TIMEOUT_LONG, retries: 3 });
			const response = await client.get('/providers/linear');
			providers = response.data as LinearProviderState['providers'];
		} catch (err) {
			if (isAxiosError(err) && err.response?.status === 404) {
				// No providers found - this is normal
				providers = [];
			} else {
				error = err instanceof Error ? err.message : 'Unknown error fetching providers';
				console.warn('[Linear Store] Error fetching providers:', error);
			}
		}

		// If we have providers, fetch teams data
		if (providers.length > 0) {
			try {
				const authToken = get(userAuthStore);
				const client = getPrivateClient(authToken, { timeout: 10000 });
				const response = await client.get('/tracker/linear/teams');
				teamsData = response.data as LinearTeamsResponse;
			} catch (err) {
				error = err instanceof Error ? err.message : 'Unknown error fetching teams';
				console.warn('[Linear Store] Error fetching teams:', error);
			}
		}

		return {
			nextUpdate: new Date(Date.now() + UPDATE_FREQUENCY).getTime(),
			needsPatch: false,
			item: {
				...$linearProviderDataModel.item,
				connected: providers.length > 0,
				providers,
				teamsData,
				error
			} as LinearProviderState
		};
	},
	handleLinearError
);

// Safe wrapper function that ensures user ID is set before loading
const safeLoadStoredLinearData = async (force: boolean = false, reload: boolean = false) => {
	try {
		await waitForAuthAndUserId().catch((err) => {
			console.debug('[Linear Store] Error waiting for auth and user id:', err);
			throw err;
		});

		console.log('[Linear Store] Auth and user ID confirmed, loading Linear data...');
		await loadStoredLinearData(force, reload);
	} catch (error) {
		console.error('[Linear Store] Error loading Linear data:', error);
	}
};

let refreshPromise: Promise<boolean> | undefined;

// Safe wrapper function that ensures user ID is set before refreshing
const safeRefreshLinearData = async (): Promise<boolean> => {
	// Prevent multiple simultaneous refresh calls
	if (refreshPromise) {
		console.log('[Linear Store] Linear refresh already in progress, returning existing promise...');
		return refreshPromise;
	}
	refreshPromise = _safeRefreshLinearData().then((result) => {
		refreshPromise = undefined;
		return result;
	});
	return refreshPromise;
};

const _safeRefreshLinearData = async (): Promise<boolean> => {
	try {
		console.log('[Linear Store] Waiting for auth...');

		await waitForAuthAndUserId().catch((err) => {
			console.debug('[Linear Store] Error waiting for auth and user id:', err);
			throw err;
		});

		console.log('[Linear Store] Auth and user ID confirmed, refreshing Linear data...');
		const result = await refreshLinearData();
		console.log('[Linear Store] Linear data refreshed:', result);
		return result;
	} catch (error) {
		console.debug('[Linear Store] Error refreshing Linear data:', error);
		return false;
	}
};

export async function disconnectLinear(providerId: string): Promise<boolean> {
	try {
		const authToken = get(userAuthStore);
		const client = getPrivateClient(authToken, { timeout: 5000 });
		const response = await client.delete('/providers/linear', {
			params: { providerId }
		});

		if (response.status !== 200) {
			throw new Error('Failed to disconnect');
		}

		await safeRefreshLinearData();
		return true;
	} catch (error) {
		console.warn('[Linear Store] Failed to disconnect Linear provider', error);
		return false;
	}
}

// Derived stores for convenience
export const hasLinearProvider = derived(
	linearProviderStore,
	($store) => $store.connected && $store.providers.length > 0
);

export const linearProviders = derived(linearProviderStore, ($store) => $store.providers);

export const linearTeamsData = derived(linearProviderStore, ($store) => $store.teamsData);

export const linearStatus = derived(
	linearProviderDataModel,
	($linearProviderDataModel) => $linearProviderDataModel.status
);

export const linearError = derived(linearProviderStore, ($store) => $store.error);

// Export for compatibility
export {
	linearProviderStore,
	linearProviderDataModel,
	refreshLinearData,
	setLinearProviderState,
	resetLinearProviderState,
	safeRefreshLinearData,
	safeLoadStoredLinearData
};
